// src/components/pages/admin-dash/AdminAuditLog.jsx
import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/AxiosInstance";
import Card from "../../ui/Card";
import Button from "../../ui/Button";
import Field from "../../ui/Field";
import Badge from "../../ui/Badge";
import AdminTable from "./AdminTable";

/**
 * Read-only viewer for AdminAuditLogResource (/admin/audit-log).
 *
 * Every sensitive admin mutation that records an audit note — user edits
 * (EditUserModal) and bulk credit grants (BulkCredits) so far — shows up
 * here, one row per affected user, most recent first.
 */
const ACTION_LABELS = {
  USER_UPDATE: "User update",
  BULK_CREDIT: "Bulk credit",
};

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function AdminAuditLog() {
  const [targetUserId, setTargetUserId] = useState("");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [didSearch, setDidSearch] = useState(false);

  const fetchEntries = async (userIdArg) => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      const uid = userIdArg !== undefined ? userIdArg : targetUserId;
      if (uid) params.targetUserId = uid;
      const res = await axiosInstance.get("/admin/audit-log", { params });
      setEntries(Array.isArray(res.data) ? res.data : []);
      setDidSearch(true);
    } catch {
      setError("Failed to load audit log.");
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  // Load the most recent entries (unfiltered) on first mount.
  useEffect(() => { fetchEntries(""); }, []); // eslint-disable-line

  const exportCsv = () => {
    if (entries.length === 0) return;
    const headers = ["id", "createdAt", "action", "actorId", "actorName", "targetUserId", "targetName", "changes", "note"];
    const lines = [headers.join(",")];
    entries.forEach((e) => {
      const row = [
        e.id, e.createdAt, ACTION_LABELS[e.action] || e.action,
        e.actor?.id, e.actor?.name,
        e.targetUserId ?? "", e.target?.name ?? "",
        e.changes, e.note,
      ].map((v) => `"${String(v ?? "").replaceAll('"', '""')}"`);
      lines.push(row.join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-log-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    { key: "createdAt", label: "When", sort: true, width: "160px", value: (e) => e.createdAt || "",
      render: (e) => <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>{fmtDate(e.createdAt)}</span> },
    { key: "action", label: "Action", sort: true, value: (e) => e.action || "",
      render: (e) => <Badge variant="gold">{ACTION_LABELS[e.action] || e.action}</Badge> },
    { key: "actor", label: "Admin", sort: true, value: (e) => e.actor?.name || "",
      render: (e) => (
        <div>
          <div style={{ color: "var(--txt)" }} className="font-medium">{e.actor?.name || "—"}</div>
          <div className="text-[11px]" style={{ color: "var(--txt-3)" }}>#{e.actor?.id ?? "?"}</div>
        </div>
      ) },
    { key: "target", label: "User", sort: true, value: (e) => e.target?.name || "",
      render: (e) => e.targetUserId
        ? (
          <div>
            <div style={{ color: "var(--txt)" }} className="font-medium">{e.target?.name || "—"}</div>
            <div className="text-[11px]" style={{ color: "var(--txt-3)" }}>#{e.targetUserId}</div>
          </div>
        )
        : <span style={{ color: "var(--txt-3)" }}>—</span> },
    { key: "changes", label: "Changes", render: (e) => (
        <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>{e.changes || "—"}</span>
      ) },
    { key: "note", label: "Note", render: (e) => (
        <span className="text-[12px]" style={{ color: "var(--txt-2)" }}>{e.note || "—"}</span>
      ) },
  ];

  return (
    <div className="space-y-4">
      <header>
        <h1 className="font-semibold mb-1" style={{ fontSize: 22, color: "var(--txt)" }}>
          Audit log
        </h1>
        <p className="text-[12px]" style={{ color: "var(--txt-2)" }}>
          Every sensitive user edit and bulk credit grant, with who did it and why.
        </p>
      </header>

      <Card variant="soft" className="!p-4">
        <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
          <Field
            label="Filter by user ID"
            type="number"
            value={targetUserId}
            onChange={(e) => setTargetUserId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchEntries()}
            placeholder="Leave blank to see everything"
            className="!mb-0"
          />
          <div className="flex items-end gap-2">
            <Button icon="search" onClick={() => fetchEntries()} loading={loading}>Search</Button>
            {targetUserId && (
              <button
                type="button"
                onClick={() => { setTargetUserId(""); fetchEntries(""); }}
                className="text-[12px] hover:underline px-1 pb-3"
                style={{ color: "var(--txt-2)" }}
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </Card>

      {error && (
        <div
          className="rounded-xl px-4 py-3 text-[13px]"
          style={{
            background: "var(--red-dim)",
            border: "1px solid var(--red-border)",
            color: "var(--red)",
          }}
        >
          {error}
        </div>
      )}

      <AdminTable
        title={didSearch ? `${entries.length} entries` : undefined}
        rightHeaderSlot={
          entries.length > 0 && (
            <Button icon="download" size="sm" variant="ghost" onClick={exportCsv}>Export CSV</Button>
          )
        }
        columns={columns}
        rows={entries}
        rowKey="id"
        loading={loading}
        emptyIcon="shield-check"
        emptyMessage="No audit entries yet."
        defaultSort={{ key: "createdAt", dir: "desc" }}
      />
    </div>
  );
}
